<template>
    <div class="multi-datetime-wrapper">
        <a href="#"
           @click.prevent.stop="open"
           class="btn-add-item">
            <i class="fas fa-calendar-alt"></i>
            {{ buttonText }}
        </a>

        <div
            class="multi-datetime-input-container vdatetime-input"
            :class="inputClass"
            :style="inputStyle"
            :id="inputId"
            v-bind="$attrs">
            <div v-if="selectedDates.length === 0" class="placeholder-text">
                {{ placeholder }}
            </div>
            <div v-else class="selected-dates-badges">
                <span
                    v-for="date in sortedSelectedDates"
                    :key="date"
                    class="selected-date-badge"
                    @click.stop="removeDate(date)">
                    {{ formatDate(date) }}
                    <i class="fas fa-times"></i>
                </span>
            </div>
        </div>

        <transition-group name="vdatetime-fade" tag="div">
            <div key="overlay" v-if="isOpen" class="vdatetime-overlay" @click.self="confirm"></div>
            <div key="popup" v-if="isOpen" class="vdatetime-popup multi-date-popup">
                <div class="vdatetime-popup__header">
                    <div v-if="title" class="vdatetime-popup__title">{{ title }}</div>
                    <div class="vdatetime-popup__year" @click="showYear">{{ currentYear }}</div>
                    <div class="vdatetime-popup__date">{{ currentMonthDate }}</div>
                </div>

                <div class="vdatetime-popup__body">
                    <datetime-year-picker 
                        v-if="step === 'year'"
                        :min-date="minDatetimeUTC"
                        :max-date="maxDatetimeUTC"
                        :year="currentYear"
                        @change="onChangeYear">
                    </datetime-year-picker>

                    <datetime-month-picker 
                        v-if="step === 'month'"
                        :min-date="minDatetimeUTC"
                        :max-date="maxDatetimeUTC"
                        :year="currentYear"
                        :month="currentMonth"
                        @change="onChangeMonth">
                    </datetime-month-picker>

                    <multi-date-calendar
                        v-if="step === 'date'"
                        :year="currentYear"
                        :month="currentMonth"
                        :selected-dates="selectedDates"
                        :min-date="minDatetimeUTC"
                        :max-date="maxDatetimeUTC"
                        :week-start="weekStart"
                        :max-selections="maxSelections"
                        :locale="locale"
                        @toggle-date="toggleDate">
                    </multi-date-calendar>
                </div>

                <div class="vdatetime-popup__actions">
                    <div class="vdatetime-popup__actions__button vdatetime-popup__actions__button--cancel" 
                         @click="cancel">
                        <slot name="button-cancel">{{ phrases.cancel }}</slot>
                    </div>
                    <div class="vdatetime-popup__actions__button vdatetime-popup__actions__button--confirm" 
                         @click="confirm">
                        <slot name="button-confirm">{{ phrases.ok }}</slot>
                    </div>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<script>
import { DateTime } from 'luxon'
import DatetimeYearPicker from 'vue-datetime/src/DatetimeYearPicker'
import DatetimeMonthPicker from 'vue-datetime/src/DatetimeMonthPicker'
import MultiDateCalendar from './MultiDateCalendar'

export default {
    name: 'MultiDatePicker',
    
    components: {
        DatetimeYearPicker,
        DatetimeMonthPicker,
        MultiDateCalendar
    },

    inheritAttrs: false,

    props: {
        value: {
            type: Array,
            default: function() {
                return []
            }
        },
        valueZone: {
            type: String,
            default: 'UTC'
        },
        inputId: {
            type: String,
            default: ''
        },
        inputClass: {
            type: [Object, Array, String],
            default: ''
        },
        inputStyle: {
            type: [Object, Array, String],
            default: ''
        },
        zone: {
            type: String,
            default: 'local'
        },
        format: {
            type: String,
            default: 'yyyy-LL-dd'
        },
        phrases: {
            type: Object,
            default: function() {
                return {
                    cancel: 'Cancel',
                    ok: 'Ok'
                }
            }
        },
        minDatetime: {
            type: String,
            default: null
        },
        maxDatetime: {
            type: String,
            default: null
        },
        weekStart: {
            type: Number,
            default: 7
        },
        title: {
            type: String,
            default: 'Select Dates'
        },
        placeholder: {
            type: String,
            default: 'Select multiple dates'
        },
        maxSelections: {
            type: Number,
            default: null
        },
        displayFormat: {
            type: String,
            default: 'count'
        },
        locale: {
            type: String,
            default: 'en'
        },
        buttonText: {
            type: String,
            default: 'Select Dates'
        }
    },

    data() {
        return {
            isOpen: false,
            selectedDates: [],
            currentYear: DateTime.local().year,
            currentMonth: DateTime.local().month,
            step: 'date'
        }
    },

    watch: {
        value: {
            handler(newValue) {
                if (Array.isArray(newValue)) {
                    this.selectedDates = newValue
                        .filter(date => this.isValidDate(date))
                        .map(date => DateTime.fromISO(date, { zone: 'UTC' }).toISODate())
                        .sort()
                } else {
                    this.selectedDates = []
                }
            },
            immediate: true
        }
    },

    computed: {
        luxonLocale() {
            // Map locale to Luxon locale codes
            return this.locale === 'en' ? 'en' : 'zh-HK'
        },

        sortedSelectedDates() {
            return [...this.selectedDates].sort()
        },

        currentMonthDate() {
            const date = DateTime.local().set({ year: this.currentYear, month: this.currentMonth })
            return date.setLocale(this.luxonLocale).toFormat('LLLL')
        },

        displayValue() {
            if (this.selectedDates.length === 0) {
                return ''
            }

            if (this.displayFormat === 'count') {
                const count = this.selectedDates.length
                if (this.locale === 'en') {
                    return `${count} date${count > 1 ? 's' : ''} selected`
                } else {
                    return `已選擇 ${count} 個日期`
                }
            }

            if (this.displayFormat === 'list') {
                return this.selectedDates
                    .map(date => DateTime.fromISO(date).setZone(this.zone).setLocale(this.luxonLocale).toFormat(this.format))
                    .join(', ')
            }

            if (this.displayFormat === 'range' && this.selectedDates.length > 0) {
                const sorted = [...this.selectedDates].sort()
                const first = DateTime.fromISO(sorted[0]).setZone(this.zone).setLocale(this.luxonLocale).toFormat(this.format)
                const last = DateTime.fromISO(sorted[sorted.length - 1]).setZone(this.zone).setLocale(this.luxonLocale).toFormat(this.format)

                if (this.selectedDates.length === 1) {
                    return first
                }

                const count = this.selectedDates.length
                if (this.locale === 'en') {
                    return `${first} - ${last} (${count} dates)`
                } else {
                    return `${first} - ${last} (${count} 個日期)`
                }
            }

            return this.selectedDates
                .map(date => DateTime.fromISO(date).setZone(this.zone).setLocale(this.luxonLocale).toFormat(this.format))
                .join(', ')
        },

        minDatetimeUTC() {
            return this.minDatetime ? DateTime.fromISO(this.minDatetime).toUTC() : null
        },

        maxDatetimeUTC() {
            return this.maxDatetime ? DateTime.fromISO(this.maxDatetime).toUTC() : null
        }
    },

    methods: {
        isValidDate(date) {
            return !!DateTime.fromISO(date).isValid
        },

        open() {
            this.isOpen = true
            // Set current view to the first selected date or today
            if (this.selectedDates.length > 0) {
                const firstDate = DateTime.fromISO(this.selectedDates[0]).setZone(this.zone)
                this.currentYear = firstDate.year
                this.currentMonth = firstDate.month
            } else {
                const now = DateTime.local()
                this.currentYear = now.year
                this.currentMonth = now.month
            }
            this.step = 'date'
        },

        close() {
            this.isOpen = false
            this.$emit('close')
        },

        formatDate(dateString) {
            return DateTime.fromISO(dateString).toFormat('yyyy-MM-dd')
        },

        removeDate(date) {
            this.toggleDate(date)
        },

        toggleDate(dateString) {
            const index = this.selectedDates.indexOf(dateString)

            if (index > -1) {
                // Remove date
                this.selectedDates = this.selectedDates.filter(d => d !== dateString)
            } else {
                // Add date
                if (this.maxSelections && this.selectedDates.length >= this.maxSelections) {
                    // Optional: show warning or remove oldest date
                    return
                }
                this.selectedDates = [...this.selectedDates, dateString].sort()
            }
        },

        confirm() {
            this.emitInput()
            this.close()
        },

        cancel() {
            // Restore original values
            if (Array.isArray(this.value)) {
                this.selectedDates = [...this.value]
            }
            this.close()
        },

        clearAll() {
            this.selectedDates = []
            this.emitInput()
        },

        emitInput() {
            const dates = this.selectedDates.map(date => {
                // Parse date-only string as UTC to prevent timezone shifting
                return DateTime.fromISO(date, { zone: 'UTC' }).setZone(this.valueZone).toISO()
            })
            this.$emit('input', dates)
            this.$emit('change', dates)
        },

        showYear() {
            this.step = 'year'
        },

        showMonth() {
            this.step = 'month'
        },

        onChangeYear(year) {
            this.currentYear = year
            this.step = 'month'
        },

        onChangeMonth(month) {
            this.currentMonth = month
            this.step = 'date'
        }
    }
}
</script>

<style scoped>
.multi-datetime-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
}

.multi-datetime-input-container {
    position: relative;
    margin-top: 10px;
}

.multi-datetime-input-container .placeholder-text {
    display: none;
}

.selected-dates-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.selected-date-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background-color: #e77109;
    color: white;
    border-radius: 3px;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.3s ease;
}

.selected-date-badge:hover {
    opacity: 0.7;
}

.selected-date-badge i {
    margin-left: 6px;
    font-size: 12px;
}

.datetime_remove {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 1;
}

.multi-date-popup {
    min-width: 320px;
}
</style>




// WEBPACK FOOTER //
// resources/assets/js/components/DatePicker/MultiDatePicker.vue